import { useBulkImport, useCategories, useLots, useProperties } from '@/api/hooks';
import { getAllErrorMessages } from '@/lib/api';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { canManage } from '@/lib/rbac';
import { useAuth } from '@/contexts/AuthContext';
import { sexLabel } from '@/lib/format';
import { cn } from '@/lib/cn';
import { Download, Upload } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

const CSV_TEMPLATE = `internal_number,sex,category_code,name,birth_date,current_weight_kg
BR-001,female,vaca_multipara,Vaca 1,2020-03-15,420
BR-002,male,touro,Touro 1,2019-01-10,680`;

interface ParsedRow {
  internal_number: string;
  sex: string;
  category_code: string;
  name?: string;
  lot_id?: number;
  breed_id?: number;
  birth_date?: string;
  current_weight_kg?: number;
}

/** Só aceita inteiro positivo — evita datas (2020-03-15) caírem em lot_id por colunas desalinhadas. */
function parseOptionalId(value: string | undefined): number | undefined {
  const v = value?.trim();
  if (!v || !/^\d+$/.test(v)) return undefined;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

function parseOptionalNumber(value: string | undefined): number | undefined {
  const v = value?.trim();
  if (!v || !/^-?\d+(\.\d+)?$/.test(v)) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function toApiRow(row: ParsedRow): Record<string, unknown> {
  const api: Record<string, unknown> = {
    internal_number: row.internal_number,
    sex: row.sex,
    category_code: row.category_code,
  };
  if (row.name) api.name = row.name;
  if (row.lot_id != null) api.lot_id = row.lot_id;
  if (row.breed_id != null) api.breed_id = row.breed_id;
  if (row.birth_date) api.birth_date = row.birth_date;
  if (row.current_weight_kg != null) api.current_weight_kg = row.current_weight_kg;
  return api;
}

function parseCsv(text: string): ParsedRow[] {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map((h) => h.trim().replace(/^\uFEFF/, ''));
  const rows: ParsedRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map((v) => v.trim());
    if (values.every((v) => !v)) continue;

    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] ?? '';
    });

    rows.push({
      internal_number: row.internal_number,
      sex: row.sex?.toLowerCase(),
      category_code: row.category_code?.toLowerCase(),
      name: row.name || undefined,
      lot_id: parseOptionalId(row.lot_id),
      breed_id: parseOptionalId(row.breed_id),
      birth_date: row.birth_date?.trim() || undefined,
      current_weight_kg: parseOptionalNumber(row.current_weight_kg),
    });
  }

  return rows;
}

function rowIssues(
  row: ParsedRow,
  line: number,
  validCategories: Map<string, 'male' | 'female'>,
  validLotIds: Set<number>,
): string[] {
  const issues: string[] = [];
  if (!row.internal_number?.trim()) {
    issues.push(`Linha ${line}: cod. interno obrigatório.`);
  }
  if (!['male', 'female'].includes(row.sex)) {
    issues.push(`Linha ${line}: sexo inválido (use male ou female).`);
  }
  if (!row.category_code || !validCategories.has(row.category_code)) {
    issues.push(`Linha ${line}: categoria inválida.`);
  } else if (row.sex && validCategories.get(row.category_code) !== row.sex) {
    issues.push(`Linha ${line}: categoria incompatível com o sexo.`);
  }
  if (row.lot_id != null && !validLotIds.has(row.lot_id)) {
    issues.push(`Linha ${line}: lote #${row.lot_id} não existe nesta propriedade (ou remova lot_id da planilha).`);
  }
  return issues;
}

export function BulkImportPage() {
  const { user } = useAuth();
  const { propertyId } = usePropertyScope();
  const bulkImport = useBulkImport();
  const { data: properties } = useProperties();
  const { data: categories = [] } = useCategories();
  const { data: lotsData } = useLots(propertyId);
  const [preview, setPreview] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState('');
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState('');

  const categoryMap = useMemo(
    () => new Map(categories.map((c) => [c.code, c.sex])),
    [categories],
  );

  const validLotIds = useMemo(
    () => new Set((lotsData?.lots ?? []).map((l) => l.id)),
    [lotsData],
  );

  const previewIssues = useMemo(
    () => preview.flatMap((row, i) => rowIssues(row, i + 1, categoryMap, validLotIds)),
    [preview, categoryMap, validLotIds],
  );

  if (!user || !canManage(user.role)) {
    return <Navigate to="/" replace />;
  }

  function handleFile(file: File) {
    setParseError('');
    setImportErrors([]);
    setSuccess('');
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseCsv(String(reader.result));
      if (!rows.length) {
        setParseError('Arquivo vazio ou formato inválido.');
        setPreview([]);
        return;
      }
      setPreview(rows);
    };
    reader.readAsText(file);
  }

  function downloadTemplate() {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'manejai-importacao-animais.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleImport() {
    if (!propertyId || !preview.length || previewIssues.length > 0) return;
    setImportErrors([]);
    setSuccess('');
    try {
      const result = await bulkImport.mutateAsync({
        property_id: propertyId,
        animals: preview.map(toApiRow),
      });
      setSuccess(result.message);
      setPreview([]);
    } catch (err) {
      setImportErrors(getAllErrorMessages(err, 'Erro na importação.'));
    }
  }

  const propertyName = properties?.properties.find((p) => p.id === propertyId)?.name;
  const femaleCategories = categories.filter((c) => c.sex === 'female');
  const maleCategories = categories.filter((c) => c.sex === 'male');

  return (
    <div>
      <PageHeader
        title="Importação em massa"
        subtitle="Migração inicial do rebanho via planilha CSV"
        actions={
          <Button variant="outline" onClick={downloadTemplate}>
            <Download className="h-4 w-4" />
            Baixar modelo
          </Button>
        }
      />

      <Alert variant="info" className="mb-6">
        Lote e raça são <strong>opcionais</strong> — você pode importar só com número, sexo e categoria.
        Use o modelo CSV sem <code>lot_id</code> se não quiser vincular pasto/curral na importação.
      </Alert>

      <Card className="mb-6">
        <CardHeader>
          <h2 className="font-medium text-manejai-forest">Códigos de categoria válidos</h2>
        </CardHeader>
        <CardBody className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-manejai-bark/50 mb-2">
              Fêmeas (sex = female)
            </p>
            <ul className="space-y-1 text-manejai-bark/80">
              {femaleCategories.map((c) => (
                <li key={c.code}>
                  <code className="text-manejai-forest bg-manejai-sand/60 px-1.5 py-0.5 rounded">{c.code}</code>
                  {' — '}{c.name}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-manejai-bark/50 mb-2">
              Machos (sex = male)
            </p>
            <ul className="space-y-1 text-manejai-bark/80">
              {maleCategories.map((c) => (
                <li key={c.code}>
                  <code className="text-manejai-forest bg-manejai-sand/60 px-1.5 py-0.5 rounded">{c.code}</code>
                  {' — '}{c.name}
                </li>
              ))}
            </ul>
          </div>
        </CardBody>
      </Card>

      {propertyId && (lotsData?.lots.length ?? 0) > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <h2 className="font-medium text-manejai-forest">Vincular a lote (opcional)</h2>
          </CardHeader>
          <CardBody className="text-sm text-manejai-bark/80">
            <p className="mb-2">
              Só adicione a coluna <code>lot_id</code> no CSV se quiser. Caso contrário, os animais
              entram sem lote e você associa depois.
            </p>
            <ul className="flex flex-wrap gap-2">
              {lotsData!.lots.map((l) => (
                <li key={l.id}>
                  <code className="text-manejai-forest bg-manejai-sand/60 px-1.5 py-0.5 rounded">
                    {l.id}
                  </code>
                  {' — '}{l.name}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <h2 className="font-medium text-manejai-forest">1. Propriedade de destino</h2>
          </CardHeader>
          <CardBody>
            {propertyName ? (
              <p className="text-sm text-manejai-bark/70">
                Use o seletor <strong>Propriedade ativa</strong> no topo da tela.
                Destino atual: <strong>{propertyName}</strong>
              </p>
            ) : (
              <p className="text-sm text-manejai-bark/60">
                Selecione a propriedade ativa no menu fixo do topo.
              </p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="font-medium text-manejai-forest">2. Enviar CSV</h2>
          </CardHeader>
          <CardBody>
            <label className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-manejai-sand bg-manejai-cream/30 px-6 py-10 cursor-pointer hover:border-manejai-sage transition-colors">
              <Upload className="h-8 w-8 text-manejai-moss" />
              <span className="text-sm text-manejai-bark/70">Clique ou arraste o arquivo CSV</span>
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              />
            </label>
          </CardBody>
        </Card>
      </div>

      {parseError && <Alert variant="error" className="mt-6">{parseError}</Alert>}
      {importErrors.length > 0 && (
        <Alert variant="error" className="mt-6">
          <p className="font-medium mb-2">
            {importErrors.length === 1
              ? importErrors[0]
              : `${importErrors.length} erros na importação:`}
          </p>
          {importErrors.length > 1 && (
            <ul className="max-h-48 overflow-y-auto space-y-1 text-sm list-disc pl-4">
              {importErrors.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}
      {previewIssues.length > 0 && (
        <Alert variant="warning" className="mt-6">
          <p className="font-medium mb-2">{previewIssues.length} problema(s) na planilha — corrija antes de importar:</p>
          <ul className="max-h-48 overflow-y-auto space-y-1 text-sm list-disc pl-4">
            {previewIssues.slice(0, 30).map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
            {previewIssues.length > 30 && (
              <li>… e mais {previewIssues.length - 30} erro(s)</li>
            )}
          </ul>
        </Alert>
      )}
      {success && <Alert variant="success" className="mt-6">{success}</Alert>}

      {preview.length > 0 && (
        <Card className="mt-6">
          <CardHeader className="flex justify-between items-center">
            <h2 className="font-medium text-manejai-forest">Pré-visualização — {preview.length} linha(s)</h2>
            <Button
              onClick={handleImport}
              loading={bulkImport.isPending}
              disabled={!propertyId || previewIssues.length > 0}
            >
              Confirmar importação
            </Button>
          </CardHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-manejai-sand text-left text-xs text-manejai-bark/60">
                  <th className="px-5 py-2">Cod. Interno</th>
                  <th className="px-5 py-2">Sexo</th>
                  <th className="px-5 py-2">Categoria</th>
                  <th className="px-5 py-2">Nome</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-manejai-sand/60">
                {preview.slice(0, 20).map((row, i) => {
                  const invalid = rowIssues(row, i + 1, categoryMap, validLotIds).length > 0;
                  return (
                    <tr key={i} className={cn(invalid && 'bg-red-50/60')}>
                      <td className="px-5 py-2">{row.internal_number}</td>
                      <td className="px-5 py-2">
                        {row.sex === 'male' || row.sex === 'female' ? sexLabel(row.sex) : row.sex || '—'}
                      </td>
                      <td className="px-5 py-2">
                        <code className={cn(invalid && 'text-red-700')}>{row.category_code || '—'}</code>
                      </td>
                      <td className="px-5 py-2">{row.name ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {preview.length > 20 && (
              <p className="px-5 py-3 text-xs text-manejai-bark/50">… e mais {preview.length - 20} linha(s)</p>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
